/**
 * Bitmart REST API Functions
 *
 * REST endpoint helpers for Bitmart spot market data.
 * @see https://developer-pro.bitmart.com/en/spot/#public-market-data
 */

import type {
  BitmartDepthRequest,
  BitmartDepthResponse,
  BitmartKlinesRequest,
  BitmartKlinesResponse,
  BitmartSymbolsDetailsResponse,
  BitmartTickerRequest,
  BitmartTickerResponse,
  BitmartTradesRequest,
  BitmartTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api-cloud.bitmart.com';

/**
 * Bitmart REST API endpoints.
 * @see https://developer-pro.bitmart.com/en/spot/#public-market-data
 */
export const bitmartRestEndpoints = {
  symbols: `${BASE_URL}/spot/v1/symbols/details`,
  depth: `${BASE_URL}/spot/quotation/v3/books`,
  klines: `${BASE_URL}/spot/quotation/v3/klines`,
  trades: `${BASE_URL}/spot/quotation/v3/trades`,
  ticker: `${BASE_URL}/spot/quotation/v3/ticker`,
} as const;

/**
 * Fetches symbol metadata including price precision.
 * @see https://developer-pro.bitmart.com/en/spot/#get-trading-pair-details
 */
export const getBitmartSymbolsDetails = async (
  options?: RestRequestOptions,
): Promise<BitmartSymbolsDetailsResponse> => {
  const response = await get<BitmartSymbolsDetailsResponse>({
    url: bitmartRestEndpoints.symbols,
    options,
  });

  return response.data;
};

/**
 * Fetches orderbook depth snapshot for a symbol.
 * @see https://developer-pro.bitmart.com/en/spot/#get-market-depth-v3
 */
export const getBitmartDepth = async (
  params: BitmartDepthRequest,
  options?: RestRequestOptions,
): Promise<BitmartDepthResponse> => {
  const response = await get<BitmartDepthResponse, BitmartDepthRequest>({
    url: bitmartRestEndpoints.depth,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches historical klines (candlesticks) for a symbol.
 * @see https://developer-pro.bitmart.com/en/spot/#get-k-line-v3
 */
export const getBitmartKlines = async (
  params: BitmartKlinesRequest,
  options?: RestRequestOptions,
): Promise<BitmartKlinesResponse> => {
  const response = await get<BitmartKlinesResponse, BitmartKlinesRequest>({
    url: bitmartRestEndpoints.klines,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches recent public trades for a symbol.
 * @see https://developer-pro.bitmart.com/en/spot/#get-recent-trades-v3
 */
export const getBitmartTrades = async (
  params: BitmartTradesRequest,
  options?: RestRequestOptions,
): Promise<BitmartTradesResponse> => {
  const response = await get<BitmartTradesResponse, BitmartTradesRequest>({
    url: bitmartRestEndpoints.trades,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * @see https://developer-pro.bitmart.com/en/spot/#get-ticker-of-a-trading-pair-v3
 */
export const getBitmartTicker = async (
  params: BitmartTickerRequest,
  options?: RestRequestOptions,
): Promise<BitmartTickerResponse> => {
  const response = await get<BitmartTickerResponse, BitmartTickerRequest>({
    url: bitmartRestEndpoints.ticker,
    params,
    options,
  });

  return response.data;
};
